import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, MagnifyingGlass } from "@phosphor-icons/react";

/*  NAS Staff → Permissions page — matches the SNS reference grid.
    Layout:
      • Header: staff name + email + role + Search filter
      • Table: two columns, "Main Permission" (with parent checkbox that
        toggles all subs) and "Sub Permission" (individual checkboxes).
      • Footer: Submit + Back.
    Persisted on the staff doc as `permissions: string[]` where each entry
    is `parent.sub` (e.g. `user.user_create`). */
export default function NasStaffPermissions() {
  const { nasId, staffId } = useParams();
  const location = useLocation();
  const nav = useNavigate();
  const [tree, setTree]           = useState([]);
  const [selected, setSelected]   = useState(new Set());
  const [saving, setSaving]       = useState(false);
  const [loading, setLoading]     = useState(true);
  const [staff, setStaff]         = useState({ name: location.state?.staffName || "" });
  const [query, setQuery]         = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [{ data: t }, { data: p }] = await Promise.all([
          http.get("/permissions/tree"),
          http.get(`/nas/${nasId}/staff/${staffId}/permissions`),
        ]);
        setTree(t.tree || []);
        setSelected(new Set(p.permissions || []));
        setStaff(p.staff || { name: location.state?.staffName || "" });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load permissions");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [nasId, staffId]);

  const toggleOne = (id) => setSelected((prev) => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });
  const toggleGroup = (parent) => setSelected((prev) => {
    const s = new Set(prev);
    const ids = parent.subs.map((sub) => `${parent.key}.${sub.key}`);
    const allOn = ids.every((id) => s.has(id));
    ids.forEach((id) => (allOn ? s.delete(id) : s.add(id)));
    return s;
  });
  const toggleAll = () => setSelected((prev) => {
    const all = tree.flatMap((p) => p.subs.map((sub) => `${p.key}.${sub.key}`));
    return prev.size >= all.length ? new Set() : new Set(all);
  });

  const submit = async () => {
    try {
      setSaving(true);
      const { data } = await http.put(`/nas/${nasId}/staff/${staffId}/permissions`, { permissions: [...selected] });
      toast.success(`Saved ${data.permissions.length} permissions`);
      nav("/nas-staff");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const totalIds = useMemo(() => tree.flatMap((p) => p.subs.map((s) => `${p.key}.${s.key}`)).length, [tree]);
  const q = query.trim().toLowerCase();
  const filtered = q ? tree.map((p) => ({ ...p, subs: p.subs.filter((s) => s.label.toLowerCase().includes(q) || p.label.toLowerCase().includes(q)) })).filter((p) => p.subs.length) : tree;

  if (loading) return <div className="text-sm text-muted-foreground p-6" data-testid="perms-loading">Loading permissions…</div>;

  return (
    <div className="space-y-4" data-testid="permissions-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 pb-3 border-b border-border">
        <div>
          <div className="uppercase-label">NAS EMPLOYEE PERMISSIONS</div>
          <h1 className="text-2xl font-display mt-1">{staff.name || "Employee"} <span className="text-sm text-muted-foreground font-mono ml-2">{staff.email}</span></h1>
          <div className="text-xs text-muted-foreground mt-1 font-mono">
            {selected.size} of {totalIds} permissions granted · role: <span className="text-primary">{staff.role || "—"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search permission…"
              data-testid="perms-search"
              className="h-9 pl-8 pr-3 text-sm bg-card border border-border rounded-sm w-64"
            />
          </div>
          <Button variant="outline" size="sm" onClick={toggleAll} data-testid="perms-toggle-all" className="rounded-sm h-9 text-xs">
            {selected.size >= totalIds ? "Uncheck all" : "Check all"}
          </Button>
        </div>
      </div>

      {/* Grid */}
      <div className="border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-[280px_1fr] bg-[hsl(var(--muted))] text-xs uppercase tracking-wide font-mono">
          <div className="py-3 px-4 border-r border-border">Main Permission</div>
          <div className="py-3 px-4">Sub Permission</div>
        </div>
        {filtered.map((parent) => {
          const ids = parent.subs.map((s) => `${parent.key}.${s.key}`);
          const parentChecked = ids.length > 0 && ids.every((id) => selected.has(id));
          const parentPartial = !parentChecked && ids.some((id) => selected.has(id));
          return (
            <div key={parent.key} className="grid grid-cols-[280px_1fr] border-t border-border" data-testid={`perm-group-${parent.key}`}>
              <label className="py-4 px-4 border-r border-border flex items-start gap-3 text-sm font-medium cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={parentChecked}
                  ref={(el) => { if (el) el.indeterminate = parentPartial; }}
                  onChange={() => toggleGroup(parent)}
                  data-testid={`perm-parent-${parent.key}`}
                  className="mt-0.5"
                />
                <span>{parent.label}</span>
              </label>
              <div className="py-3 px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-2 gap-x-6 text-sm">
                {parent.subs.map((sub) => {
                  const id = `${parent.key}.${sub.key}`;
                  return (
                    <label key={id} className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={selected.has(id)}
                        onChange={() => toggleOne(id)}
                        data-testid={`perm-${id}`}
                      />
                      <span>{sub.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="py-12 text-center text-sm text-muted-foreground">No permissions match "{query}"</div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-center gap-3 pt-2">
        <Button
          onClick={submit}
          disabled={saving}
          data-testid="perms-submit"
          className="rounded-sm h-10 px-8 bg-primary hover:bg-primary/90"
        >
          <FloppyDisk size={14} className="mr-2" /> {saving ? "Saving…" : "Submit"}
        </Button>
        <Link to="/nas-staff" data-testid="perms-back">
          <Button variant="outline" className="rounded-sm h-10 px-8 border-destructive text-destructive hover:bg-destructive/10">
            <ArrowLeft size={14} className="mr-2" /> Back
          </Button>
        </Link>
      </div>
    </div>
  );
}
